import { Spinner } from '@/components/ui/spinner';
import { CreateWordModal } from '@/components/words/editor/create-word-modal';
import { FunctionLetter } from '@/components/words/function-letter';
import { RoundTagGroup } from '@/components/words/round-tag-group';
import { ToCheckBadge } from '@/components/words/to-check-badge';
import { WordsFilterPanel } from '@/components/words/words-filter-panel';
import { displayNoyauPonderation } from '@/lib/noyaux';
import { useResultViewMode } from '@/lib/result-view-mode';
import { useWordsNav } from '@/lib/words-nav';
// Left panel of the words layout: brand header, live search input, filters, sort, and scrollable word list.
import { WORD_SORT_LABELS, WORD_SORT_OPTIONS, type WordSort } from '@/lib/words-query';
import type { Word } from '@/schemas/word';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

/** Wraps the first occurrence of `query` in `text` with a `<mark>` for search highlighting. */
function highlightMatch(text: string, query: string) {
  if (!query.trim()) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = query.toLowerCase().trim();
  const idx = lower.indexOf(q);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

/** Sidebar with live search, filters, sort, and a scrollable list of word results. */
export function WordsSidebar() {
  const nav = useWordsNav();
  const [viewMode, setViewMode] = useResultViewMode();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentId = pathname.startsWith('/words/') ? pathname.slice('/words/'.length) : '';
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Infinite scroll: load the next page once the container is scrolled near
  // its bottom. A plain scroll-position check (rather than IntersectionObserver
  // on a sentinel) — more reliable across layouts and doesn't depend on the
  // compositor recomputing intersection on every scroll frame.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = nav;
  const resultsRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = resultsRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Reuses the word-link CreateWordModal: on success, navigate to the new word's page.
  function handleNewWord(word: Word) {
    setCreateOpen(false);
    nav.pushWord(word.id);
  }

  const isRefined = nav.query.length > 0 || nav.activeFilterCount > 0;

  return (
    <aside className="words-sidebar">
      <div className="words-sidebar__head">
        <div className="words-sidebar__brand">
          <img
            className="words-sidebar__brand-logo"
            src="/assets/logo-cercle.png"
            alt=""
            aria-hidden="true"
          />
          <span className="words-sidebar__brand-bold">patrickmathieu</span>
          <span className="words-sidebar__brand-light">singularité</span>
          <span className="words-sidebar__brand-sep">·</span>
          <span className="words-sidebar__brand-word">codex</span>
        </div>

        <div className="words-searchbar">
          {/* biome-ignore lint/a11y/useSemanticElements: <search> is not yet in @types/react intrinsics */}
          <div className="words-search" role="search">
            <div className="words-search__shell">
              <div className="words-search__leading" aria-hidden="true">
                <span className="material-symbols-rounded">search</span>
              </div>
              <input
                className="words-search__input"
                type="search"
                value={nav.query}
                onChange={(e) => nav.setQuery(e.target.value)}
                aria-label="Rechercher un mot"
                placeholder="Rechercher un mot…"
              />
              {nav.query && (
                <button
                  className="words-search__clear"
                  aria-label="Effacer"
                  onClick={() => nav.setQuery('')}
                  type="button"
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
              )}
            </div>
          </div>

          <button
            type="button"
            className={`words-filter-toggle${filtersOpen ? ' is-open' : ''}`}
            aria-label="Filtres"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              tune
            </span>
            {nav.activeFilterCount > 0 && (
              <span className="words-filter-toggle__badge">{nav.activeFilterCount}</span>
            )}
          </button>
        </div>

        {filtersOpen && <WordsFilterPanel />}
      </div>

      <div className="words-sidebar__meta">
        <div className="words-sidebar__meta-left">
          <span className="words-sidebar__meta-label">{isRefined ? 'Résultats' : 'Mots'}</span>
          <span className="words-sidebar__meta-count">
            {nav.results.length} {nav.results.length === 1 ? 'mot' : 'mots'}
          </span>
        </div>
        <div className="words-sidebar__meta-right">
          <label className="words-sort">
            <select
              className="words-sort__select"
              aria-label="Trier les résultats"
              value={nav.sort}
              onChange={(e) => nav.setSort(e.target.value as WordSort)}
            >
              {WORD_SORT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {WORD_SORT_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="words-view-toggle"
            aria-label={viewMode === 'mosaic' ? 'Afficher en liste' : 'Afficher en mosaïque'}
            aria-pressed={viewMode === 'mosaic'}
            onClick={() => setViewMode(viewMode === 'mosaic' ? 'list' : 'mosaic')}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {viewMode === 'mosaic' ? 'view_list' : 'grid_view'}
            </span>
          </button>
        </div>
      </div>

      <nav className="words-results" aria-label="Résultats de recherche" ref={resultsRef}>
        {nav.isLoading ? (
          <div className="words-results__loading">
            <Spinner size="sm" label="Chargement des résultats" />
          </div>
        ) : nav.results.length === 0 ? (
          <p className="words-empty">
            Aucun résultat
            {nav.activeFilterCount > 0 && (
              <button type="button" className="words-empty__reset" onClick={nav.resetFilters}>
                Réinitialiser les filtres
              </button>
            )}
          </p>
        ) : (
          <>
            {nav.results.map((word) => {
              const fullTitle = word.title;
              const isActive = currentId === word.id;
              return (
                <Link
                  key={word.id}
                  to="/words/$wordId"
                  params={{ wordId: word.id }}
                  className={`words-res${isActive ? ' is-active' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    setViewMode('list');
                    nav.pushWord(word.id, { silent: true });
                  }}
                >
                  <FunctionLetter fonction={word.fonction} placeholder />
                  <span className="words-res__title">{highlightMatch(fullTitle, nav.query)}</span>
                  <span className="words-res-noyaux">
                    <ToCheckBadge toCheck={word.toCheck} reasons={word.reasons} />
                    <RoundTagGroup
                      noyauPonderation={displayNoyauPonderation(
                        word.noyauPonderation,
                        word.dominantNoyauCode ?? null,
                      )}
                      size="compact"
                    />
                  </span>
                </Link>
              );
            })}
            {nav.capped ? (
              <p className="words-results__more">
                Affinez la recherche pour voir plus de résultats.
              </p>
            ) : (
              nav.isFetchingNextPage && (
                <div className="words-results__sentinel" aria-hidden="true">
                  <Spinner size="sm" label="Chargement…" />
                </div>
              )
            )}
          </>
        )}
      </nav>

      <div className="words-sidebar__foot">
        <button
          type="button"
          className="words-sidebar__new"
          aria-label="Nouveau"
          onClick={() => setCreateOpen(true)}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            add
          </span>
          <span className="words-sidebar__new-label">Nouveau</span>
        </button>
      </div>

      {createOpen && (
        <CreateWordModal
          selectedText=""
          onConfirm={handleNewWord}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </aside>
  );
}
